import { useQuery, useQueryClient } from '@tanstack/react-query';
import Page from '../../../components/PageWrapper';
import { IActivityStreams, ITableFilterItem } from '../../../resources/interfaces';
import { useMemo, useState } from 'react';
import GTable, { GTableActionColumn } from '../../../components/elements/GTable/GTable';
import { Link, useSearchParams } from 'react-router-dom';
import {
  archiveRNPL,
  getAllRNPL
} from '../../../api/requests';
import PopOnMe from '../../../components/elements/poponme';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import TableFilter from '../../../components/elements/GTable/InGTableComponents/TableFilter';
import useUserAccount from '../../../hooks/userAccountHook';
import {
  capitalizeSentence,
  getFormattedDate,
  getPhoneNumberFromShortCodeFormat,
  showToast
} from '../../../utils/miscFunctions';
import TableFilterForm from '../../../components/elements/GTable/InGTableComponents/TableFilter/TableFilterForm';
import useRoute from '../../../hooks/routesHook';
import useModal from '../../../hooks/modalHook';
import AddNewRnPl from './addNewRnPl';
import PolicyStatus from '../../../components/elements/PolicyStatus';
import EditReviewStage from './EditReviewStage';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../states/slices/resourceSlice';
import ActionConfirmation from '../../../components/elements/ActionConfirmation';
import { IVehicle } from '../../../models/vehicle/IVehicle';
import { ICustomer } from '../../../models/customer/ICustomer';
import Button from '../../../components/form-control/Button';
import { Archive, Cog, FileSearch } from 'lucide-react';

export interface IRNPL {
  activityStreams: IActivityStreams[];
  applicationNumber: string;
  approvedBy: string | null;
  approvedOn: string | null;
  countryId: number;
  createdOn: string;
  id: number;
  ownerConsent: boolean;
  repairCost: number;
  repairDescription: string;
  repairType: string;
  reviewStage: string;
  updatedOn: string;
  vehicles: IVehicle;
  customer: ICustomer
}

interface ICell {
  cell: { value: string | null };
  row: { original: { firstname: string, lastname: string, id: string, customer: { firstName: string, lastName: string, id: string } } };
}

interface ICellOriginal {
  row: { original: IRNPL };
}

const RnPl = () => {
  const { user } = useUserAccount();
  const { getDashboardBase } = useRoute();
  const [showPerPage, setShowPerPage] = useState<number>(100);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tableFilters, setTableFilters] = useState<ITableFilterItem[]>(getDefaultFilters());
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1
  const [currentPage, setCurrentPage] = useState<number>(
    searchParams.get('page') ? Number(searchParams.get('page')) : 0
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams({ page: String(page) });
  };
  const [loading, setLoading] = useState(true);
  const [rnplCustomers, setRnplCustomers] = useState<IRNPL[]>([]);
  const queryClient = useQueryClient();

  const displayRNPLCustomer = () => {
    getAllRNPL(currentPage, showPerPage, countryId)
      .then((response: any) => {
        setRnplCustomers(response?.data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const { } = useQuery({
    queryKey: ['displayRNPLCustomer', countryId],
    queryFn: displayRNPLCustomer
  });

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['displayRNPLCustomer'] });
  };

  const editReviewStage = (rnpl: IRNPL) => {
    rnplCustomers &&
      updateModalStates({
        show: true,
        size: 'md',
        children: (
          <EditReviewStage
            rnplApplicant={rnpl}
            onProceed={() => {
              closeModal();
              invalidateQueries();
            }}
            onClose={closeModal}
          />
        ),
        showCloseButton: false,
        classes: '!p-0 r-[5px]',
      });
  };

  const archiveSelectedRNPL = (rnpl: IRNPL) => {
    updateModalStates({
      show: true,
      size: 'sm',
      children: (
        <ActionConfirmation
          action="archive"
          type='warning'
          resourceType={''}
          resourceName={''}
          onClose={closeModal}
          onConfirm={() => {
            archiveRNPL(rnpl.id)
              .then((response: any) => {

                closeModal();
                showToast.success(response?.message);

                invalidateQueries();

              })
              .catch((error: any) => {
                console.log(error);
                closeModal();
                showToast.error(error?.description ?? 'Could not complete the action. Please try again.');
              });
          }}
        />
      )
    });
  }
  const getTableRowActions = (rnpl: IRNPL) => {
    const actions = [];

    rnpl.reviewStage !== 'APPROVED' &&
      actions.push({
        label: 'Review RNPL Application',
        icon: <FileSearch className="h-4 w-4" />,
        callback: () => {
          editReviewStage(rnpl);
        },
        data: data
      });

    actions.push({
      label: 'Archive',
      icon: <Archive className="h-4 w-4" />,
      callback: () => {
        archiveSelectedRNPL(rnpl)
      },
      data: data
    });

    return actions;
  };

  const columns = useMemo(() => {
    const baseColumns = [

      {
        Header: 'Application Number',
        accessor: 'applicationNumber',
        Cell: (row: ICell) => {
          return (
            <Link
              to={`${getDashboardBase()}/rnpl/${row.row?.original?.id}`}
              target="blank_"
              className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">
              {row.cell.value ? row.cell.value : '-'}
            </Link>
          );
        }
      },
      {
        Header: 'Applicant',
        accessor: 'customer',
        Cell: ({ row }: ICell) => (
          <Link
            to={`${getDashboardBase()}/customers/${row.original.customer.id}`}
            target="blank_"
            className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">
            {capitalizeSentence(row.original.customer.firstName)}{' '}
            {capitalizeSentence(row.original.customer.lastName) ?? '-'}
          </Link>
        )
      },
      {
        Header: 'Phone',
        accessor: 'customer.phone',
        Cell: (row: ICell) => {
          return (
            <span>
              {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
            </span>
          );
        }
      },
      {
        Header: 'Status',
        accessor: 'reviewStage',
        Cell: (row: ICell) => {
          return (
            <PolicyStatus
              status={
                row.cell.value ? capitalizeSentence(row.cell.value.split('_').join(' ')) : '-'
              }
              classes="rounded-lg px-2  w-full"
            />
          );
        }
      },
      {
        Header: 'Vehicle Number',
        accessor: 'vehicles.registrationNumber',
        Cell: (row: ICell) => {
          return <span>{row.cell.value ?? '-'}</span>;
        }
      },
      {
        Header: 'Repair Cost',
        accessor: 'repairCost',
        Cell: (row: ICell) => {
          return <span>{row.cell.value ? row.cell.value : '-'}</span>;
        }
      },
      {
        Header: 'Repair Type',
        accessor: 'repairType',
        Cell: (row: ICell) => {
          return (
            <span>
              {row.cell.value ? capitalizeSentence(row.cell.value.split('_').join(' ')) : '-'}
            </span>
          );
        }
      },
      {
        Header: 'Created on',
        accessor: 'createdOn',
        Cell: (row: ICell) => {
          return (
            <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>
          );
        }
      },
      {
        id: 'Action',
        Cell: (row: ICellOriginal) => {
          return (
            <GTableActionColumn actions={getTableRowActions(row.row.original)} />
          );
        }
      }
    ];

    return [
      {
        Header: 'M',
        columns: baseColumns
      }
    ];
  }, []);

  const { data } = useQuery({
    queryKey: ['displayRNPLCustomer'],
    queryFn: displayRNPLCustomer
  });

  function getDefaultFilters() {
    const filters: ITableFilterItem[] = [
      {
        name: 'create_date',
        value: '',
        type: 'dateRange',
        placeholder: 'Select start date',
        label: 'Date Range'
      }
    ];

    return filters;
  }

  const getOtherActions = () => {
    let actions = [];

    if (user.isFuelManager() || user.isFuelAdmin()) {
      actions.push({
        label: 'Export sales',
        callback: () => { }
      });
    }

    return actions;
  };

  const createRNPLHandler = () => {
    if (!user.canCreateRNPL()) {
      showToast.error('You do not have permission to create RNPL');
      return;
    }
    updateModalStates({
      show: true,
      children: (
        <AddNewRnPl
          onClose={closeModal}
          onProceed={() => {
            queryClient.invalidateQueries({ queryKey: ['displayRNPLCustomer'] });
            closeModal();
          }}
        />
      ),
      size: '2xl',
      showTitleSection: true,
      title: 'Repair Now, Pay Later Application Form'
    });
  };

  return (
    <Page title="RNPL" className="">
      <div className="p-5 bg-white dark:bg-darkBg rounded-lg shadow-md dark:shadow-darkBg-shadow">
        <div>
          <div className="flex items-center justify-between flex-wrap gap-5">
            <div>
              <div className="inline-flex items-center gap-2 mb-2 dark:text-darkBg-mainText">
                <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                <h2 className="font-semibold text-3xl">Repair now, Pay later</h2>
              </div>

              <p className="text-sm text-slate-400">View a list of all Repair now, Pay later applications.</p>
            </div>

            <div className="sm:flex justify-end pt-2 md:pt-0">
              <Button
                label={'New Application'}
                icon={<Cog size={26} className='mr-2' />}
                onClick={createRNPLHandler}
                bgColor='primary'
              // disabled={!user?.canCreateRNPL()}
              />
            </div>
          </div>

          <div className="">
            <GTable
              columns={columns}
              data={rnplCustomers ?? []}
              classes="h-fit"
              isRowLinked={false}
              tableType={'Application(s)'}
              showSearch={false}
              loading={loading}
              onPageChange={handlePageChange}
              showPerPage={showPerPage}
              onShowPerPageChange={(page: number) => setShowPerPage(page)}
              actionElement={
                getOtherActions().length > 0 && (
                  <PopOnMe actions={getOtherActions()}>
                    <div className="cursor-pointer">
                      <EllipsisVerticalIcon className="h-6 w-6 " />
                    </div>
                  </PopOnMe>
                )
              }
              showFilter={
                <>
                  <TableFilter
                    openFilter={openFilter}
                    onFilterChange={(filterState: boolean) => setOpenFilter(filterState)}
                    selectedFilters={tableFilters}
                    onRemoveFilter={(filter: ITableFilterItem, index: number) => {
                      const _tableFilters: ITableFilterItem[] = [...tableFilters];
                      let thisFilter = { ...tableFilters[index] };
                      thisFilter = { ..._tableFilters[index], value: '' };
                      _tableFilters[index] = thisFilter;
                      setTableFilters([..._tableFilters]);
                    }}
                    content={
                      <TableFilterForm
                        filters={tableFilters}
                        getValues={(filters: ITableFilterItem[]) => {
                          setTableFilters(filters);
                          setOpenFilter(false);
                        }}
                        onResetFilters={() => setTableFilters(getDefaultFilters())}
                      />
                    }
                  />
                </>
              }
            />
          </div>
        </div>
      </div>
      <ModalComponent />
    </Page>
  );
};

export default RnPl;
